/**
 * Finds the dealer override configured for the "agendar/:concesionaria" route param.
 * Only exact slugs match (e.g. "2051-san-jose-chiapa"); the plain dealer number does not.
 * @param {string | null | undefined} routeParam
 * @param {Object<string, {dealerNumber: number, contactEmails: string[]}>} overrides
 * @returns {{dealerNumber: number, contactEmails: string[]} | null}
 */
export const findDealerOverride = (routeParam, overrides) => {
  if (typeof routeParam !== "string") return null;

  const normalizedSlug = routeParam.trim().toLowerCase();
  if (normalizedSlug === "") return null;

  return overrides[normalizedSlug] ?? null;
};

/**
 * Returns the override contact emails when configured, otherwise the Octagon emails.
 * @param {{contactEmails: string[]} | null} dealerOverride
 * @param {string[] | string} octagonEmails
 * @returns {string[] | string}
 */
export const resolveContactEmails = (dealerOverride, octagonEmails) => {
  if (dealerOverride && dealerOverride.contactEmails.length > 0) {
    return dealerOverride.contactEmails;
  }

  return octagonEmails;
};

const OCTAGON_CONTACT_EMAILS_INDEX = 8;

/**
 * Parses the dealer contact emails from the Octagon "contactos" field (index 8, ";"-separated).
 * Keeps the legacy return values: [] when contactos is null, "" when it has too few fields.
 * @param {string | null} octagonContactos
 * @returns {string[] | string}
 */
const parseOctagonContactEmails = (octagonContactos) => {
  if (octagonContactos === null || octagonContactos === undefined) return [];

  const contactFields = octagonContactos.split(",");
  if (contactFields.length <= OCTAGON_CONTACT_EMAILS_INDEX) return "";

  return contactFields[OCTAGON_CONTACT_EMAILS_INDEX].split(";").map((email) => email.trim());
};

/**
 * Returns the emails that receive the dealer copies of "Citas de servicio".
 * @param {string | null} octagonContactos
 * @param {{contactEmails: string[]} | null} dealerOverride
 * @returns {string[] | string}
 */
export const getDealerContactEmails = (octagonContactos, dealerOverride) =>
  resolveContactEmails(dealerOverride, parseOctagonContactEmails(octagonContactos));

/**
 * Returns the normalized override slug for the route param, or null when it has no override.
 * @param {string | null | undefined} routeParam
 * @param {Object<string, {dealerNumber: number, contactEmails: string[]}>} overrides
 * @returns {string | null}
 */
export const getDealerOverrideSlug = (routeParam, overrides) =>
  findDealerOverride(routeParam, overrides) ? routeParam.trim().toLowerCase() : null;
